import AnchorText from '../AnchorText';
import Button, { BtnClose } from '../Button';
import { PopCcontainer, PopupTitle } from '../Css/Pop';
import FormGroup from '../Form/FormGroup';
import LabelText from '../Form/LabelText';
import Col from '../Grid/Col';
import Row from '../Grid/Row';
import AddressButton from '../Input/AddressButton';
import InputText from '../Input/InputText';
import Modal from '../Modal';
import Select from '../Select';
import { Tit } from '../Titles';
import { PublicationControllerService } from '@/__generated__/FrontApi';
import DatabaseAgreement from '@/components/DatabaseAgreement';
import ErrorMessage from '@/components/Form/ErrorMessage';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { emailRegex, mobileRegex } from '@/helpers/ValidationHelper';
import { FormButton, RightCol } from '@/page-blocks/auth/AuthCommon';
import LocalStorage from '@/services/LocalStorage';
import { usePopupStore } from '@/stores/PopupStore';
import { useUserStore } from '@/stores/UserStore';
import React, { FC, useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

export interface ModalProps {
  isOpen?: boolean;
  // 모달 바깥쪽 누를시 닫을수 있는지 여부
  dismissable?: boolean;
  // 닫힘요청시 콜백
  onRequestClose: () => void;
  maxWidth?: string;
}

interface DatabaseLayerProps extends ModalProps {
  PopSid: string | number;
  PopTitle: string;
  PopCnt: string | number;
}

const TxtItem = styled.div`
  display: flex;
  .bar {
    line-height: 56px;
    text-align: right;
    width: 23px;
  }
`;

const AgreeHead = styled.div`
  line-height: 1.6;
  padding-top: 40px;
  padding-bottom: 19px;
  border-bottom: 1px solid #b7b8ba;
`;
const OpenButton = styled(AnchorText)`
  color: #828385;
`;
const AgreementItem = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;
const AgreementContainer = styled.div`
  ${Row} {
    display: block;
    padding-top: 16px;
    &.all-chk {
      border-bottom: 1px solid #e5e6e8;
      margin-top: 8px;
      padding: 16px 0;
    }

    .box {
      background: #f8f9fa;
      margin-top: 16px;
      padding: 24px;
    }
    .blit-list {
      font-size: 14px;
      line-height: 1.7;

      &::before {
        top: 10px;
      }

      & > li {
        margin-bottom: 10px;

        &:last-child {
          margin-bottom: 0;
        }
      }
      dl {
        display: flex;

        dt {
          width: 80px;
        }
        dd {
          width: calc(100% - 80px);
          font-weight: 100;
        }
      }
    }
  }

  ${Button} {
    margin-top: 80px;
  }
`;

const PopContent = styled.div`
  padding: 32px 0 96px;
  max-width: 544px;
  margin: 0 auto;

  .col-wrap {
    display: flex;
    align-items: center;
    margin: 0 -16px;
    flex-wrap: wrap;

    .col {
      width: 50%;
      padding: 0 16px;
    }
  }

  .form-item {
    padding: 13px 0;
  }

  ${breakpoint(`mobile`)} {
    .col-wrap {
      .col {
        width: 100%;
      }
    }

    ${FormGroup} {
      .col {
        width: 100%;
        margin-bottom: 24px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
`;

const InfoBox = styled.div`
  padding-top: 15px;
`;
interface PublicationApplyData {
  boardIndexNumber: string | number;
  userId: string;
  zipCode: string;
  address: string;
  addressDetail: string;
  firstRegisterDate: string;
  name: string;
  position: string;
  userType: string;
  contact: string;
  email: string;
  dataCnt: string;
  studentCnt: string;
  studyCnt: string;
  purpose: string;
  wishList: string;
  isAgreeYn1: boolean;
  isAgreeYn2: boolean;
}

const DatabaseLayerPopup: FC<DatabaseLayerProps> = ({
  onRequestClose,
  ...props
}) => {
  const popupStore = usePopupStore();
  const userStore = useUserStore();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<PublicationApplyData>({
    defaultValues: {
      isAgreeYn1: false,
      isAgreeYn2: false,
    },
  });

  const agreeAll = watch(`agreeAll`);

  useEffect(() => {
    // 사용자 정보가 로딩되면 (로그인 상태면) 기본값 세팅
    if (userStore.user) {
      setValue(`userId`, userStore.user.userId);
      setValue(`name`, userStore.user.name);
      setValue(`email`, userStore.user.email);
      setValue(`contact`, userStore.user.mobilePhoneNumber.replace(/-/g, ``));
    }

    setValue(`position`, LocalStorage.getItem(`position`));
    setValue(`zipCode`, LocalStorage.getItem(`zipCode`));
    setValue(`address`, LocalStorage.getItem(`address`));
    setValue(`addressDetail`, LocalStorage.getItem(`addressDetail`));
  }, [setValue, userStore.user]);

  const onSubmitForm: SubmitHandler<PublicationApplyData> = useCallback(
    async ({ isAgreeYn1, isAgreeYn2, ...formData }) => {
      // console.log(formData, isAgreeYn1, isAgreeYn2);

      try {
        const {
          resultCode,
          resultMessage,
        } = await PublicationControllerService.applyPublicationUsingPost({
          isAgreeYn1: isAgreeYn1 ? `Y` : `N`,
          isAgreeYn2: isAgreeYn2 ? `Y` : `N`,
          ...formData,
        });
        // 성공후 폼 리셋
        popupStore.show(`신청되었습니다.`);

        if (userStore.user) {
          setValue(`userId`, userStore.user.userId);
          setValue(`name`, userStore.user.name);
          setValue(`email`, userStore.user.email);
          setValue(
            `contact`,
            userStore.user.mobilePhoneNumber.replace(/-/g, ``),
          );
        }
        LocalStorage.setItem(`position`, formData.position);
        LocalStorage.setItem(`zipCode`, formData.zipCode);
        LocalStorage.setItem(`address`, formData.address);
        LocalStorage.setItem(`addressDetail`, formData.addressDetail);

        reset();
        onRequestClose();
      } catch (e) {
        console.error(e);
        popupStore.show(`우편 신청에 실패했습니다. 다시 시도해주세요.`);
      }
    },
    [popupStore, reset],
  );

  return (
    <Modal onRequestClose={onRequestClose} maxWidth="880px" {...props}>
      <PopCcontainer>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <PopupTitle>
            <h2>
              <Tit size="s3" color="sky">
                아동권리 교육자료 우편 신청하기
              </Tit>
            </h2>
            <BtnClose onClick={() => onRequestClose()}>
              <span className="a11y">닫기</span>
            </BtnClose>
          </PopupTitle>
          <InfoBox>
            유니세프는 아동권리교육 자료를 제작하여 무상으로 배포하고 있습니다.
            지원 가능 수량이 한정되어 있어 한 기관에서 중복 신청하실 수 없는 점
            양해 부탁드립니다. 자료 수량이 부족한 경우 PDF 파일을 다운로드 받아
            사용하시길 권장드립니다.
          </InfoBox>
          <PopContent>
            <FormGroup className="FormGroup">
              <LabelText>신청자료명</LabelText>
              <div className="form-item">
                <Tit size="s4-1" color="sky">
                  {props.PopTitle}
                </Tit>
                {props.PopCnt && (
                  <span> (최대 {props.PopCnt}권 신청 가능)</span>
                )}
                <input
                  type="hidden"
                  name="boardIndexNumber"
                  ref={register}
                  value={props.PopSid}
                />
                <input type="hidden" name="userId" ref={register} />
              </div>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>구분</LabelText>
              <div className="col-wrap">
                <div className="col">
                  <Select
                    name="userType"
                    ref={register({
                      required: {
                        value: true,
                        message: `구분을 선택해주세요.`,
                      },
                      validate: (value) =>
                        value !== `` || `구분을 선택해주세요.`,
                    })}
                  >
                    <option value="">선택</option>
                    <option value="학생">학생</option>
                    <option value="부모">부모</option>
                    <option value="선생님">선생님</option>
                    <option value="기타">기타</option>
                  </Select>
                </div>
                <div>
                  {errors.userType && (
                    <ErrorMessage>{errors.userType.message}</ErrorMessage>
                  )}
                </div>
              </div>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>성명·단체명</LabelText>
              <Row>
                <InputText
                  name="name"
                  ref={register({
                    required: {
                      value: true,
                      message: `성명·단체명을 입력해주세요.`,
                    },
                  })}
                />
              </Row>
              <Row>
                {errors.name && (
                  <ErrorMessage>{errors.name.message}</ErrorMessage>
                )}
              </Row>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>소속</LabelText>
              <InputText
                name="position"
                placeholder="소속 입력"
                ref={register({
                  required: {
                    value: true,
                    message: `소속을 입력해주세요.`,
                  },
                })}
              />
              <Row>
                {errors.position && (
                  <ErrorMessage>{errors.position.message}</ErrorMessage>
                )}
              </Row>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>휴대폰번호</LabelText>
              <Row>
                <InputText
                  name="contact"
                  placeholder="휴대폰번호 입력 ( &lsquo;-&rsquo; 제외 )"
                  ref={register({
                    required: {
                      value: true,
                      message: `휴대폰번호를 입력해주세요.`,
                    },
                    pattern: {
                      value: mobileRegex,
                      message: `올바른 휴대폰번호가 아닙니다.`,
                    },
                    validate: {
                      isValidRange: (value) =>
                        (value.substr(0, 3) === `010` && value.length === 11) ||
                        (value.substr(0, 3) !== `010` && value.length === 10) ||
                        `휴대폰번호 자릿수를 확인해주세요`,
                    },
                  })}
                />
              </Row>
              <Row>
                {errors.contact && (
                  <ErrorMessage>{errors.contact.message}</ErrorMessage>
                )}
              </Row>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>이메일</LabelText>
              <Row>
                <InputText
                  name="email"
                  ref={register({
                    required: {
                      value: true,
                      message: `이메일을 입력해주세요.`,
                    },
                    pattern: {
                      value: emailRegex,
                      message: `올바른 이메일 형식이 아닙니다.`,
                    },
                  })}
                />
              </Row>
              <Row>
                {errors.email && (
                  <ErrorMessage>{errors.email.message}</ErrorMessage>
                )}
              </Row>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>주소</LabelText>
              <Row>
                <Col desktop="auto">
                  <InputText
                    name="zipCode"
                    ref={register({
                      required: {
                        value: true,
                        message: `주소를 입력해주세요.`,
                      },
                    })}
                    readOnly
                  />
                </Col>
                <RightCol desktop="none">
                  <AddressButton
                    onSelectAddress={(address) => {
                      setValue(`zipCode`, address.zonecode);
                      setValue(`address`, address.address);
                    }}
                    renderButton={(buttonProps) => (
                      <FormButton {...buttonProps}>주소검색</FormButton>
                    )}
                  />
                </RightCol>
              </Row>
              <Row>
                <InputText name="address" ref={register} readOnly />
              </Row>
              <Row>
                <InputText name="addressDetail" ref={register} />
              </Row>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>신청 부수</LabelText>
              <div className="col-wrap">
                <div className="col">
                  <TxtItem>
                    <InputText
                      name="dataCnt"
                      ref={register({
                        required: {
                          value: true,
                          message: `최소 1부 이상 신청 가능합니다.`,
                        },
                        pattern: {
                          value: /[0-9]/g,
                          message: `숫자만 입력해주세요.`,
                        },
                        validate: (value) =>
                          Number(value) <= Number(props.PopCnt) ||
                          `${props.PopCnt}부까지 신청 가능합니다.`,
                      })}
                      placeholder="0"
                    />
                    <span className="bar">부</span>
                  </TxtItem>
                </div>
                {errors.dataCnt && (
                  <ErrorMessage>{errors.dataCnt.message}</ErrorMessage>
                )}
              </div>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText require>사용 목적</LabelText>
              <Row>
                <Select
                  name="purpose"
                  ref={register({
                    required: {
                      value: true,
                      message: `사용 목적을 선택해주세요.`,
                    },
                  })}
                >
                  <option value="">선택</option>
                  <option value="아동 대상으로 아동권리교육자료 활용">
                    아동 대상으로 아동권리교육자료 활용
                  </option>
                  <option value="의무이행자(성인) 대상으로 아동권리교육자료 활용">
                    의무이행자(성인) 대상으로 아동권리교육자료 활용
                  </option>
                  <option value="교육시설 내 비치">교육시설 내 비치</option>
                  <option value="아동권리 및 유엔아동권리협약 학습">
                    아동권리 및 유엔아동권리협약 학습
                  </option>
                  <option value="기타">기타</option>
                </Select>
              </Row>
              <Row>
                {errors.purpose && (
                  <ErrorMessage>{errors.purpose.message}</ErrorMessage>
                )}
              </Row>
            </FormGroup>
            <FormGroup className="FormGroup">
              <div className="col-wrap">
                <div className="col">
                  <LabelText require>교육대상 수</LabelText>
                  <TxtItem>
                    <InputText
                      name="studentCnt"
                      ref={register({
                        required: {
                          value: true,
                          message: `교육대상 수를 입력해주세요.`,
                        },
                        pattern: {
                          value: /^[0-9]/g,
                          message: `숫자만 입력해주세요.`,
                        },
                      })}
                      placeholder="숫자 입력"
                    />
                    <span className="bar">명</span>
                  </TxtItem>
                  {errors.studentCnt && (
                    <ErrorMessage>{errors.studentCnt.message}</ErrorMessage>
                  )}
                </div>

                <div className="col">
                  <LabelText require>교육횟수</LabelText>
                  <TxtItem>
                    <InputText
                      name="studyCnt"
                      ref={register({
                        required: {
                          value: true,
                          message: `교육횟수를 입력해주세요.`,
                        },
                        pattern: {
                          value: /^[0-9]/g,
                          message: `숫자만 입력해주세요.`,
                        },
                      })}
                      placeholder="숫자 입력"
                    />
                    <span className="bar">회</span>
                  </TxtItem>
                  {errors.studyCnt && (
                    <ErrorMessage>{errors.studyCnt.message}</ErrorMessage>
                  )}
                </div>
              </div>
            </FormGroup>
            <FormGroup className="FormGroup">
              <LabelText>유니세프에 바라는 자료</LabelText>
              <InputText
                name="wishList"
                ref={register}
                placeholder="희망 자료 입력"
              />
            </FormGroup>
            <AgreeHead>
              <Tit size="s3">개인정보 수집이용동의</Tit>
            </AgreeHead>
            <AgreementContainer className="agreement-container">
              <DatabaseAgreement
                allChk
                name="policyAgree"
                ref={register({
                  required: {
                    value: true,
                    message: `이용약관에 동의해주세요`,
                  },
                })}
                policies={[
                  {
                    required: true,
                    label: `교육자료 발송 및 확인을 위한 개인정보 수집 이용 동의`,
                    content1: `항목`,
                    content2: `이름, 소속, 이메일주소, 주소`,
                    content3: `보유기간`,
                    content4: `배송 확인 후 즉시 파기`,
                  },
                  {
                    required: true,
                    label: `아동권리 교육 및 행사 안내를 위한 개인정보 수집 이용 동의`,
                    content1: `항목`,
                    content2: `이름, 소속, 이메일주소, 주소`,
                    content3: `보유기간`,
                    content4: `2년`,
                  },
                ]}
              />

              <Button full type="submit">
                자료 신청하기
              </Button>
            </AgreementContainer>
          </PopContent>
        </form>
      </PopCcontainer>
    </Modal>
  );
};

export default DatabaseLayerPopup;
